export type EffectiveModulePermission = 'EDITOR' | 'VISOR' | null;

const MODULE_ADMIN_ROLES = new Set(['SUPER_ADMIN', 'ADMINISTRADOR']);
const VIEW_ONLY_ROLES = new Set(['VISOR', 'VIEWER', 'VIEW_ONLY']);

export const normalizeModuleAuthorizationName = (value: unknown): string =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();

const normalizeRole = (value: unknown): string => String(value || '').trim().toUpperCase();

const storedModulePermission = (user: any, moduleName: string): EffectiveModulePermission => {
    if (!Array.isArray(user?.accessible_modules)) return null;
    const expected = normalizeModuleAuthorizationName(moduleName);
    for (const rawEntry of user.accessible_modules) {
        const [rawModule, rawPermission = 'VISOR'] = String(rawEntry || '').split(':', 2);
        if (normalizeModuleAuthorizationName(rawModule) !== expected) continue;
        return normalizeRole(rawPermission) === 'EDITOR' ? 'EDITOR' : 'VISOR';
    }
    return null;
};

/**
 * Resolves module permission without widening institutional authority.
 * A view-only institutional role can never become an editor through a module flag.
 */
export const getEffectiveModulePermission = (user: any, moduleName: string): EffectiveModulePermission => {
    if (!user) return null;
    const role = normalizeRole(user.role);
    if (MODULE_ADMIN_ROLES.has(role)) return 'EDITOR';

    const stored = storedModulePermission(user, moduleName);
    if (!stored) return null;
    if (stored === 'EDITOR' && VIEW_ONLY_ROLES.has(role)) return 'VISOR';
    return stored;
};

export const canViewAuthorizedModule = (user: any, moduleName: string): boolean =>
    getEffectiveModulePermission(user, moduleName) !== null;

export const canEditAuthorizedModule = (user: any, moduleName: string): boolean =>
    getEffectiveModulePermission(user, moduleName) === 'EDITOR';

export const hasViewerModulePermissionConflict = (role: unknown, accessibleModules: unknown): boolean =>
    VIEW_ONLY_ROLES.has(normalizeRole(role))
    && Array.isArray(accessibleModules)
    && accessibleModules.some((entry) => {
        const [, rawPermission = 'VISOR'] = String(entry || '').split(':', 2);
        return normalizeRole(rawPermission) === 'EDITOR';
    });

export const downgradeEditorModulePermissions = (accessibleModules: unknown): string[] => {
    if (!Array.isArray(accessibleModules)) return [];
    return accessibleModules.map((entry) => {
        const [rawModule, rawPermission = 'VISOR'] = String(entry || '').split(':', 2);
        return normalizeRole(rawPermission) === 'EDITOR' ? `${rawModule}:VISOR` : String(entry);
    });
};

export const isUserAuthorizedForCommission = (
    user: any,
    commission: { id: string; vicepresidency_id?: string | null }
): boolean => {
    if (!user || !commission?.id) return false;
    const role = normalizeRole(user.role);
    const scope = String(user.org_scope || '').trim().toUpperCase();
    if (MODULE_ADMIN_ROLES.has(role) || scope === 'GLOBAL') return true;
    if (scope === 'VP') return !!user.vicepresidency_id && user.vicepresidency_id === commission.vicepresidency_id;
    if (scope === 'COORDINATOR') {
        return (Array.isArray(user.accessible_instances) && user.accessible_instances.includes(commission.id))
            || (!!commission.vicepresidency_id
                && Array.isArray(user.accessible_vps)
                && user.accessible_vps.includes(commission.vicepresidency_id));
    }
    return (user.commission_id || user.instance_id) === commission.id;
};
